import { useTranslation } from "react-i18next";

import { formatChannelValue } from "../lib/format";

/**
 * Degrees of wheel rotation at full lock.
 *
 * The recording gives steering as a percentage of the car's lock but never states what
 * that lock is, and it varies by car. This is a representative figure for a GT or
 * prototype, chosen so the graphic moves the way the driver's hands did. The numeric
 * readout beside it is the value that is actually in the file.
 */
const LOCK_DEGREES = 240;

interface Props {
  /** "Lap 7", or "Cyril L7" once more than one driver is on screen. */
  label: string;
  colour: string;
  /** Percentage of full lock; negative is left. */
  steering: number | null;
  /** Percentages. */
  throttle: number | null;
  brake: number | null;
  gear: number | null;
  speedKph: number | null;
}

/**
 * The driver's hands and feet at one point on the track.
 *
 * A steering trace tells you how much lock was applied but not what it looked like;
 * reading "-38%" and picturing the wheel is work the eye should not have to do. Shown
 * one per compared lap, the difference in hand position at the same point on the track
 * is immediate in a way two overlaid lines are not.
 */
export function SteeringWheel({
  label,
  colour,
  steering,
  throttle,
  brake,
  gear,
  speedKph,
}: Props) {
  const { t } = useTranslation();
  const angle = ((steering ?? 0) / 100) * LOCK_DEGREES;
  const unavailable = steering === null;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="flex items-end gap-1.5">
        <svg viewBox="-52 -52 104 104" className="h-20 w-20" aria-hidden>
          <g
            transform={`rotate(${angle})`}
            style={{ opacity: unavailable ? 0.25 : 1 }}
          >
            <circle
              r={40}
              fill="none"
              stroke="#52525b"
              strokeWidth={9}
              strokeLinecap="round"
            />
            {/* Top marker: without it a rotated ring looks identical at any angle. */}
            <path
              d="M -11 -38.4 A 40 40 0 0 1 11 -38.4"
              fill="none"
              stroke={colour}
              strokeWidth={9}
              strokeLinecap="round"
            />
            <path
              d="M -35 -3 H -9 M 35 -3 H 9 M 0 8 V 34"
              stroke="#52525b"
              strokeWidth={8}
              strokeLinecap="round"
            />
            <circle r={9} fill="#3f3f46" />
          </g>
        </svg>

        <PedalBar
          value={throttle}
          colour="#22c55e"
          label={t("workspace.inputs.throttle")}
        />
        <PedalBar value={brake} colour="#ef4444" label={t("workspace.inputs.brake")} />
      </div>

      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colour }} />
        <span className="whitespace-nowrap text-zinc-300">{label}</span>
      </div>
      <div className="flex gap-2 text-[11px] tabular-nums text-zinc-500">
        <span>{formatChannelValue(steering, 100)}%</span>
        <span>{gear === null ? "—" : `G${gear.toFixed(0)}`}</span>
        <span>{speedKph === null ? "—" : `${speedKph.toFixed(0)} km/h`}</span>
      </div>
    </div>
  );
}

function PedalBar({
  value,
  colour,
  label,
}: {
  value: number | null;
  colour: string;
  label: string;
}) {
  const filled = Math.max(0, Math.min(100, value ?? 0));

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-16 w-2.5 overflow-hidden rounded-sm bg-zinc-800">
        <div
          className="absolute bottom-0 w-full transition-[height] duration-75"
          style={{ height: `${filled}%`, backgroundColor: colour }}
        />
      </div>
      <span className="text-[9px] uppercase text-zinc-600">{label}</span>
    </div>
  );
}
